import React, { useMemo } from 'react';
import MowLogoInline from "../assets/mow-full-logo-inline.svg";
import MowLogo from "../assets/mow-logo-icon.svg";

import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { ThemeProvider, createTheme, styled } from '@mui/material/styles';
import styles from "../styles/components/login-wrapper.module.scss";

import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { ErrorMessage } from '../components/error-message';
import { StyledCard } from '../components/mui-card';
import { StyledContainer } from '../components/mui-styled-container';
import { AppLogo } from './app-logo';
import { Img } from './image';

const drawerWidth: number = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

export const PoweredByLink = () => {
  return (
    <p className={styles.inlineLogo}>Powered by 
      <a href="https://www.metoceanworks.com/">
        <Img src={MowLogo} alt='logo' width={24} height={24} />
        <Img src={MowLogoInline} alt='logo inline' height={24} />
      </a>
    </p>
  )
}

export const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const defaultTheme = createTheme();

interface LoginPageWrapperProps {
  children: string | React.ReactNode;
  title: string | React.ReactNode;
  error?: string[]
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void
  fullHeight?: boolean
  fullWidth?: boolean
}
export const LoginPageWrapper = ({
  children,
  title,
  error = [],
  onSubmit,
  fullHeight = false,
  fullWidth = false,
}: LoginPageWrapperProps) => {

  const showResendConfirmation = error.join('').indexOf('email has not been confirmed') !== -1

  const hasTwoCollumns = useMemo(() => {
    const childArray = React.Children.toArray(children)
    return childArray.length >= 2
  }, [children])

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box className={styles.outerWrapper}>
        <CssBaseline />
        <AppBar position="absolute">
          <Toolbar
              variant="dense"
              sx={{
                pr: '12px !important', // keep right padding when drawer closed
                backgroundColor: '#010824',
              }}
            >
            <AppLogo />
          </Toolbar>
        </AppBar>
        <Box
          className={styles.mainContent}
          component="main"
        >
          <StyledContainer direction="column" justifyContent="space-between">
            <StyledCard variant="outlined" className={classNames(styles.wrapper, {
                [styles.twoColumns]: hasTwoCollumns,
                [styles.fullWidth]: fullWidth,
              })}>
              <div>
                <div>
                  <Typography
                    component="h1"
                    variant="h4"
                    sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                  >
                    {title}
                  </Typography>
                  <ErrorMessage message={error} />
                  {showResendConfirmation &&
                    <Link
                      to={`/confirm-email`}
                      // variant="body2"
                      // sx={{ alignSelf: 'flex-end', marginTop: '-16px' }}
                    >
                      Generate new confirmation email
                    </Link>
                  }
                  {hasTwoCollumns && Array.isArray(children) ? children[0] : null}
                </div>
                {hasTwoCollumns && <PoweredByLink />}
              </div>
              <Box
                component={onSubmit ? "form" : "div"}
                onSubmit={onSubmit}
                noValidate
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  gap: 2,
                  position: 'relative'
                }}
              >
                {hasTwoCollumns && Array.isArray(children) ? children[1] : children}
              </Box>
              {!hasTwoCollumns && <p className={styles.inlineLogo}>Powered by 
                  <a href="https://www.metoceanworks.com/">
                    <Img src={MowLogo} alt='logo' width={24} height={24} />
                    <Img src={MowLogoInline} alt='logo inline' height={24} />
                  </a>
                </p>}
            </StyledCard>
          </StyledContainer>
        </Box>
      </Box>
    </ThemeProvider>
  )
}